import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import ConfigurationLocalAssaysAssaysLaboratoryModal from '@/modules/configurationModule/detail/local-assays/assays-laboratory/components/modal/configuration-local-assays-assays-laboratory-modal.vue'
import CodificationSearcherModal from '@/modules/configurationModule/detail/samples/CodificationSearcherModal.vue'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { BaseCrud } from '@/shared/models/cruds.models'
import ModalWrapper from '@/components/ModalWrapper.vue'
import { ConfigurationLocalAssaysAssaysLaboratoryGridState, initialConfigurationLocalAssaysAssaysLaboratoryGridState } from '@/modules/configurationModule/detail/local-assays/assays-laboratory/components/grid/configuration-local-assays-assays-laboratory-grid.model'
import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import { ConfigurationAssayLaboratoryResponse, ConfigurationAssayLaboratoryCreateRequest, ConfigurationAssayLaboratoryPatchInBatchRequest } from '@/modules/configurationModule/detail/local-assays/assays-laboratory/services/configuration-assay-laboratory/configuration-assay-laboratory.dto'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        ConfigurationLocalAssaysAssaysLaboratoryModal,
        CodificationSearcherModal
    }
})
export default class ConfigurationLocalAssaysAssaysLaboratoryGrid extends BaseCrudServerSideComponent<ConfigurationAssayLaboratoryResponse> implements BaseCrud {
    @Ref() readonly assaysLaboratoryModalWrapper!: typeof ModalWrapper

    state: ConfigurationLocalAssaysAssaysLaboratoryGridState

    get canUpdate(): boolean {
        return this.state.selectedRows.length === 1
    }

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    constructor() {
        super()
        this.state = initialConfigurationLocalAssaysAssaysLaboratoryGridState()
    }

    /// SERVICES CALLS ///

    private async _createAssayLaboratory(createRequest: ConfigurationAssayLaboratoryCreateRequest): Promise<ConfigurationAssayLaboratoryResponse | null> {
        const response = await this.$_toCallUnique(this.state.service.create(this.state.laboratoryId, createRequest))
        if (response) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
            return response
        }
        return null
    }

    private async _patchAssaysLaboratory(batchRequests: ConfigurationAssayLaboratoryPatchInBatchRequest[]): Promise<EntityIdentifierWithErrorResponse[] | null> {
        if (batchRequests) {
            const response = await this.$_toCallUnique(this.state.service.patch(this.state.laboratoryId, batchRequests))
            if (response) {
                const elements = batchRequests.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    private async _deleteAssaysLaboratory(requestDelete: EntityIdentifiersRequest): Promise<EntityIdentifierWithErrorResponse[] | null> {
        const ids = requestDelete?.entityIdentifiers
        if (ids?.length) {
            const response = await this.$_toCallUnique(this.state.service.delete(this.state.laboratoryId, requestDelete))
            if (response) {
                const elements = ids.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    /// GRID OPERATIONS ///

    async activateDeactivateSelection(active: boolean, assaysLaboratory: ConfigurationAssayLaboratoryResponse[]): Promise<void> {
        if (assaysLaboratory?.length) {
            const batchRequests = assaysLaboratory.filter(assayLaboratory => assayLaboratory.active !== active)
                .map(assayLaboratory => ({
                    id: assayLaboratory.id,
                    active
                }))
            if (await this._patchAssaysLaboratory(batchRequests)) {
                this.reloadData()
            }
        }
    }

    async createOperation(createRequest: ConfigurationAssayLaboratoryCreateRequest): Promise<void> {
        if (createRequest) {
            if (await this._createAssayLaboratory(createRequest)) {
                this.reloadData()
            }
        }
    }

    async patchOperation(batchRequest: ConfigurationAssayLaboratoryPatchInBatchRequest): Promise<void> {
        if (batchRequest) {
            if (await this._patchAssaysLaboratory([batchRequest])) {
                this.reloadData()
            }
        }
    }

    private async _showModalAdviceWhenDelete(): Promise<boolean> {
        const labelConfirmation: string = this.$tc('configuration.grid.deleteConfirmation')
        const selectedRowsSize: number = this.state?.selectedRows?.length
        const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
        const labelQuestion: string = this.$tc('configuration.grid.deleteQuestion')
        const labelAcceptAction: string = this.$tc('configuration.actions.delete')
        return this.$_showConfirmationModal(
            `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelQuestion}`,
            labelAcceptAction
        )
    }

    async deleteOperation(requestDelete: EntityIdentifiersRequest): Promise<void> {
        if (requestDelete) {
            if (await this._showModalAdviceWhenDelete()) {
                if (await this._deleteAssaysLaboratory(requestDelete)) {
                    this.reloadData()
                }
            }
        }
    }

    /// GRID EVENT HANDLERS ///

    onUpdate(assayLaboratory: ConfigurationAssayLaboratoryResponse = this.state.gridApi?.getSelectedRows()[0]): void {
        if (assayLaboratory) {
            this.showModal(this.$tc('configuration.actions.update'), assayLaboratory, this.assaysLaboratoryModalWrapper)
        }
    }

    async onDelete(): Promise<void> {
        if (this.state.selectedRows.length) {
            const requestDelete: EntityIdentifiersRequest = {
                entityIdentifiers: this.state.selectedRows.map(assay => assay.id)
            }
            await this.deleteOperation(requestDelete)
        }
    }

    onAdd(): void {
        this.showModal(this.$tc('configuration.actions.add'), null, this.assaysLaboratoryModalWrapper)
    }

    async onActivate(): Promise<void> {
        await this.activateDeactivateSelection(true, this.state.selectedRows)
    }

    async onDeactivate(): Promise<void> {
        await this.activateDeactivateSelection(false, this.state.selectedRows)
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$tc('configuration.actions.update'),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => this.onUpdate(params?.node?.data)
            },
            {
                name: this.$tc('configuration.actions.delete'),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: this.onDelete
            },
            {
                name: params?.node?.data?.active ? this.$tc('configuration.actions.deactivate') : this.$tc('configuration.actions.activate'),
                cssClasses: this.state.selectedRows.length ? ['not-show'] : [''],
                action: () => this.activateDeactivateSelection(!params?.node?.data?.active, [params?.node?.data])
            },
            {
                name: this.$tc('configuration.actions.activate'),
                cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                action: this.onActivate
            },
            {
                name: this.$tc('configuration.actions.deactivate'),
                cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                action: this.onDeactivate
            }
        ]
    }
}
